import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { CustomCurrencyPipe } from '@shared/pipe/custom-currency/custom-currency.pipe';
import { BaseChartComponent } from '@shared/component/charts/base-chart/base-chart.component';
import { COLORS, PALETTE_PRIMARY } from 'taxtank-core';
import { color } from 'highcharts';

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PieChartComponent extends BaseChartComponent implements OnChanges {
  // field name for column value
  @Input() bindValue: string;
  @Input() bindSeriesData = '';
  @Input() text?: string;
  // chart height
  @Input() height: number = this.viewPortService.isMobile ? 300 : 200;

  ngOnChanges(): void {
    this.initChart();
  }

  initChart(): void {
    // if (!this.data.length) {
    //   return;
    // }

    this.buildData();
    // set options declared in base class
    this.setDefaultOptions(this.customCurrencyPipe);
    // set options s
    this.setOptions(this.isCurrency, this.customCurrencyPipe);
  }

  buildData(): void {
    this.chartData = this.data.map((item: any, index: number) => {
      if (this.bindSeriesData) {
        let value = 0;
        item[this.bindSeriesData].forEach((serie: any) => {
          value = value + Math.abs(serie[this.bindValue]);
        });
        return {
          name: item[this.bindSeriesName],
          tooltip: item.tooltip,
          value: value
        };
      }
      return {
        name: item[this.bindSeriesName],
        y: Math.abs(item[this.bindValue]),
        tooltip: item.tooltip,
        dataLabels: {
          style: {
            textOutline: `1px ${color(PALETTE_PRIMARY[index])}`
          }
        }
      };
    });
  }

  setOptions(isCurrency: boolean, customCurrencyPipe: CustomCurrencyPipe): void {
    // set chart type
    this.chartOptions.chart.type = 'pie';

    // show/hide Y axis
    this.chartOptions.yAxis.visible = false;

    // show legend
    this.chartOptions.legend = {
      enabled: true,
      align: 'left',
      layout: 'vertical',
      verticalAlign: 'middle',
      itemMarginBottom: 20,
      width: 150
    };

    // set chart colors
    this.chartOptions.colors = PALETTE_PRIMARY;

    // set chart height
    this.chartOptions.chart.height = this.height;

    // set tooltip template
    this.setTooltipFormatter((context): string =>
      `<span style="font-size: 14px; color: ${context.point.color}">●</span>
       <span style="font-size: 14px;">
         ${context.point.name}: ${isCurrency ? customCurrencyPipe.transform(context.point.y, { decimals: 0 }) : context.point.y}
       </span>`);

    this.chartOptions.series = [{
      size: 130,
      innerSize: 110,
      showInLegend: true,
      borderWidth: 0,
      data: this.chartData,
      dataLabels: {
        formatter: function (): string {
          return this.y;
        },
        enabled: true,
        color: this.viewPortService.isMobile ? COLORS.secondary : COLORS.white,
        distance: this.viewPortService.isMobile ? 25 : -25,
        style: {
          fontSize: this.viewPortService.isMobile ? '12px' : '10px',
          fontWeight: 'normal'
        }
      }
    }];

    if (this.text) {
      const text = this.text;
      // centered text inside the chart
      this.chartOptions.chart.events = {
        render: function () {
          const series = this.series[0];

          if (!series) {
            return;
          }

          const [centerX, centerY] = series.center;
          if (!this.centerText) {
            // highcharts says to save custom elements to rerender inside the chart
            // https://www.highcharts.com/docs/advanced-chart-features/renderer
            this.centerText = this.renderer.text(text, this.plotLeft + centerX, this.plotTop + centerY, true)
              .add()
              .attr({ align: 'center' });

            this.centerText.toFront();
          } else {
            this.centerText.attr({
              x: this.plotLeft + centerX,
              y: this.plotTop + centerY
            });
          }
        }
      };
    }

    this.chartOptions.responsive = {
      rules: [{
        condition: {
          callback: () => this.viewPortService.isMobile
        },
        chartOptions: {
          legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom'
          },
          chart: {
            height: 'auto'
          }
        }
      }]
    };
  }
}
